import {
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';

import { AuthService } from '../auth/services/auth.service';
import { BACKGROUND_REQUEST } from './background-request';
import { LoaderService } from '../../shared/services/loader.service';

let refreshRequest$: Observable<string> | null = null;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const loader = inject(LoaderService);



  const skipAuthentication =
    req.headers.has('X-Skip-Interceptor') ||
    req.url.includes('/login') ||
    req.url.includes('/refresh-token');

  if (skipAuthentication) {
    return next(req);
  }

  const background = req.context.get(BACKGROUND_REQUEST);
  if (!background) loader.show();

  const token = localStorage.getItem('token');

  const authenticatedRequest = token
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      })
    : req;

  return next(authenticatedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || req.url.includes('/logout')) {
        return throwError(() => error);
      }

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        authService.logout();
        return throwError(() => error);
      }

      /*
       * Only the first 401 creates the refresh request.
       * Other 401 requests subscribe to the same observable.
       */
      if (!refreshRequest$) {
        refreshRequest$ = authService.useRefreshToken(refreshToken).pipe(
          map((response: any) => {
            const newToken = response?.data?.access_token;
            const newRefreshToken = response?.data?.refresh_token;

            if (!newToken) {
              throw new Error(
                'Refresh-token response does not contain an access token'
              );
            }

            localStorage.setItem('token', newToken);

            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken);
            }

            return newToken;
          }),

          catchError((refreshError: unknown) => {
            console.error(
              '[AUTH] Refresh token expired or invalid.',
              refreshError
            );

            authService.logout();

            /*
             * This error is delivered to every request waiting for
             * refreshRequest$. Consequently, every request reaches
             * its outer finalize().
             */
            return throwError(() => refreshError);
          }),

          finalize(() => {
            refreshRequest$ = null;
          }),

          /*
           * Share one refresh HTTP call between all requests that
           * received 401 simultaneously.
           */
          shareReplay({
            bufferSize: 1,
            refCount: false,
          })
        );
      }

      return refreshRequest$.pipe(
        switchMap((newToken) => {
          const retryRequest = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          });

          return next(retryRequest);
        })
      );
    }),

    /*
     * This finalize belongs to the original intercepted request.
     * It executes after success, normal error, refresh success,
     * or refresh failure.
     */
    finalize(() => {
      if (!background) loader.hide();
    })
  );
};
